import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject, switchMap } from 'rxjs';
import { FilterOperators } from 'src/app/common/enums';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { UserService } from 'src/app/data/user.service';
import { FilterType } from 'src/app/list-filter/enums/filter-type.enum';
import { FilterService } from 'src/app/list-filter/services/filter.service';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';
import { AuthService } from 'src/app/core/authentication/auth.service';

@Injectable()
export class AnnexListFilterService extends FilterService {
  private translateService = inject(TranslateService);
  private dictionaryService = inject(DictionaryService);
  private authService = inject(AuthService);
  private employerSubject: Subject<string> = new Subject<string>();
  private companySubject: Subject<string> = new Subject<string>();

  constructor() {
    const userService = inject(UserService);
    super(FilterTypeEnum.AnnexesList, userService);

    const isAdmin = this.authService.isAdmin();

    const annexStatuses$ = this.dictionaryService.getAnnexStatuses();

    this.config = [
      {
        id: 1,
        name: 'employerId',
        label: this.translateService.instant('Employer'),
        property: 'EmployerId',
        operator: FilterOperators.Equal,
        default: true,
        type: FilterType.Autocomplete,
        datasource$: this.employerSubject.asObservable().pipe(
          switchMap((value: string) => (value ? this.dictionaryService.getEmployers(value) : of([]))),
        ),
        searchTextChangeCallback: (value: any) => this.employerSubject.next(value),
      },
      {
        id: 2,
        name: 'employerObjectId',
        label: this.translateService.instant('EmployerObject'),
        property: 'EmployerObjectId',
        operator: FilterOperators.Equal,
        default: true,
        type: FilterType.SearchableMultiselect,
        getData: (value: string, page: number, pageSize: number) => this.dictionaryService.getEmployerObjectsPaged(value, page, pageSize),
      },
      {
        id: 3,
        name: 'agreementTypeId',
        label: this.translateService.instant('AgreementType'),
        property: 'AgreementTypeId',
        operator: FilterOperators.Equal,
        default: true,
        type: FilterType.Select,
        datasource$: this.dictionaryService.getAgreementTypes(),
      },
      {
        id: 4,
        name: 'lastName',
        label: this.translateService.instant('LastName'),
        property: 'LastName',
        default: true,
        operator: FilterOperators.Contains,
        type: FilterType.Text,
      },
      {
        id: 5,
        name: 'firstName',
        label: this.translateService.instant('WL-FirstName'),
        property: 'FirstName',
        default: true,
        operator: FilterOperators.Contains,
        type: FilterType.Text,
      },
      {
        id: 6,
        name: 'pesel',
        label: this.translateService.instant('Pesel'),
        property: 'Pesel',
        default: true,
        operator: FilterOperators.StringEqual,
        type: FilterType.Text,
      },
      {
        id: 7,
        name: 'companyId',
        label: this.translateService.instant('Company'),
        property: 'CompanyId',
        operator: FilterOperators.Equal,
        type: FilterType.Autocomplete,
        datasource$: this.companySubject.asObservable().pipe(
          switchMap((value: string) => (value ? this.dictionaryService.getCompanies(value) : of([]))),
        ),
        searchTextChangeCallback: (value: any) => this.companySubject.next(value),
      },
      {
        id: 9,
        name: 'startDateFrom',
        label: this.translateService.instant('AnnexRegistry.StartDateFrom'),
        property: 'StartDate',
        operator: FilterOperators.DateGreaterThanOrEqual,
        type: FilterType.Date,
      },
      {
        id: 10,
        name: 'endDateTo',
        label: this.translateService.instant('AnnexRegistry.EndDateTo'),
        property: 'EndDate',
        operator: FilterOperators.DateLessThanOrEqual,
        type: FilterType.Date,
      },
      {
        id: 11,
        name: 'annexStatusId',
        label: this.translateService.instant('AnnexRegistry.AnnexStatus'),
        property: 'AnnexStatusId',
        operator: FilterOperators.Equal,
        type: FilterType.Select,
        datasource$: annexStatuses$,
      },
      {
        id: 12,
        name: 'annexNumber',
        label: this.translateService.instant('AnnexRegistry.AnnexNumber'),
        property: 'AnnexNumber',
        operator: FilterOperators.Equal,
        type: FilterType.Text,
      },
      ...(isAdmin ? [
        {
          id: 14,
          name: 'annexSyncStatusId',
          label: this.translateService.instant('AnnexRegistry.ExternalSyncStatus'),
          property: 'AnnexSyncStatusId',
          operator: FilterOperators.Equal,
          type: FilterType.Select,
          datasource$: this.dictionaryService.getAnnexSyncStatuses(),
        },
        {
          id: 13,
          name: 'isConsistent',
          label: this.translateService.instant('AnnexRegistry.IsConsistent'),
          property: 'IsConsistent',
          operator: FilterOperators.Equal,
          type: FilterType.Select,
          datasource$: this.booleanFilter(),
        },
      ] : []),
    ];
  }
}
